/* eslint-disable react/style-prop-object */
import React, { ReactElement, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { delay } from "helpers/app";
import { calculateDiscount } from "helpers/generic";

import { isNil } from "ramda";

import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import Input from "@mui/material/Input";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Slider from "@mui/material/Slider";
import Stack from "@mui/material/Stack";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import { useTheme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Unstable_Grid2";
import useMediaQuery from "@mui/material/useMediaQuery";

import { volumesSteps } from "constants/addons";

import SectionsBreadCrumbs from "components/shared/breadCrumbs/SectionsBreadCrumbs";
import CheckCoupon from "components/shared/coupon/CheckCoupon";
import AppModal from "components/shared/modal/AppModal";
import UserWaiting from "components/shared/progressBar/UserWaiting";

import {
 postAddVolume,
 postCheckVolumeName,
 postCheckVolumePrice
} from "redux/handlers/addonsHandle";
import { getAllServersNotPaged, getServersLocations } from "redux/handlers/serverHandler";

import { getUserBalance } from "redux/selectors/userSelector";

import { useAppDispatch } from "hooks/reduxHook";

import { IAllServers, ILocations } from "types/api/serversApiInterface";

const VolumesWizard = (): ReactElement => {
 const intl = useIntl();
 const navigate = useNavigate();
 const dispatch = useAppDispatch();
 const theme = useTheme();
 const desktopViewPort = useMediaQuery(theme.breakpoints.up("lg"));

 const userBalance = useSelector(getUserBalance);

 const [activeStep, setActiveStep] = useState<number>(0);
 const [openModal, setOpenModal] = useState<boolean>(false);
 const [isLoading, setIsLoading] = useState<boolean>(true);
 const [nameChecked, setNameChecked] = useState<boolean>(false);
 const [stepLoading, setStepLoading] = useState<boolean>(false);
 const [locationList, setLocationList] = useState<Array<ILocations>>([]);
 const [servers, setServers] = useState<Array<IAllServers>>([]);
 const [firstCheck, setFirstCheck] = useState<boolean>(false);
 const [price, setPrice] = useState<number>(0);
 const [loading, setLoading] = useState<boolean>(false);
 const [couponData, setCouponData] = useState<{ id: number; percent: number } | null>(null);

 useEffect(() => {
  (async () => {
   setLocationList(await dispatch(getServersLocations(1)));
   setServers(await dispatch(getAllServersNotPaged()));
   setIsLoading(false);
  })();
 }, []);

 const { control, formState, reset, watch, getValues, setValue } = useForm({
  defaultValues: {
   name: "",
   datacenter: "",
   format: "",
   size: 10,
   server_id: 0
  }
 });

 const handleOpen = () => setOpenModal(true);
 const handleClose = () => setOpenModal(false);
 const handleReset = () => {
  reset();
  setNameChecked(false);
  setFirstCheck(false);
  setActiveStep(0);
 };

 const handleNext = () => {
  if (activeStep === 3) handleCheckPrice();
  setActiveStep((prevActiveStep) => prevActiveStep + 1);
 };

 const handleBack = () => {
  setActiveStep((prevActiveStep) => prevActiveStep - 1);
 };

 const handleConfirm = async () => {
  setLoading(true);
  if (couponData) {
   getValues("server_id") !== 0
    ? await dispatch(
       postAddVolume(
        getValues("name"),
        getValues("format"),
        getValues("datacenter"),
        getValues("size"),
        getValues("server_id"),
        couponData.id
       )
      )
    : await dispatch(
       postAddVolume(
        getValues("name"),
        getValues("format"),
        getValues("datacenter"),
        getValues("size"),
        0,
        couponData.id
       )
      );
  } else {
   getValues("server_id") !== 0
    ? await dispatch(
       postAddVolume(
        getValues("name"),
        getValues("format"),
        getValues("datacenter"),
        getValues("size"),
        getValues("server_id")
       )
      )
    : await dispatch(
       postAddVolume(
        getValues("name"),
        getValues("format"),
        getValues("datacenter"),
        getValues("size")
       )
      );
  }
  await delay(3000);
  setLoading(false);
  handleClose();
  handleReset();
  navigate("/addons/volumes");
 };

 const handleCheckName = async () => {
  setStepLoading(true);
  setNameChecked(await dispatch(postCheckVolumeName(getValues("name"))));
  setFirstCheck(true);
  setStepLoading(false);
 };

 const handleCheckPrice = async () => {
  setStepLoading(true);
  setPrice(await dispatch(postCheckVolumePrice(getValues("size"))));
  setStepLoading(false);
 };

 const handleSliderChange = (event: Event, newValue: number | number[]) => {
  setValue("size", newValue as number);
 };

 const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  setValue("size", event.target.value === "" ? 10 : Number(event.target.value));
 };

 const handleBlur = () => {
  if (watch("size") <= 10) {
   setValue("size", 10);
  } else if (watch("size") > 1000) {
   setValue("size", 1000);
  }
 };

 const renderStepContent = (index: number) => {
  switch (index) {
   case 0:
    return (
     <Controller
      name="name"
      control={control}
      render={({ field }) => (
       <TextField
        {...field}
        fullWidth={true}
        onChange={(e) => {
         setValue("name", e.target.value);
         setFirstCheck(false);
        }}
        value={watch("name")}
        label={intl.formatMessage({ id: "volumes.name" })}
        error={formState.isDirty && !!formState?.errors?.name}
        InputLabelProps={{ shrink: true }}
        sx={{ my: 2 }}
        autoComplete="new-password"
       />
      )}
     />
    );
   case 1:
    return (
     <Controller
      name="datacenter"
      control={control}
      render={({ field }) => (
       <Autocomplete
        fullWidth={true}
        autoHighlight
        sx={{ my: 2 }}
        onChange={(e, value) => {
         setValue("datacenter", value?.id || "");
        }}
        options={locationList.map((element) => {
         return { label: element.city, id: element.name };
        })}
        value={locationList
         .map((element) => {
          return { label: element.city, id: element.name };
         })
         .find((element) => element.id === getValues("datacenter"))}
        renderInput={(params) => (
         <TextField
          {...params}
          {...field}
          label={<FormattedMessage id="volumes.add.datacenter" />}
          error={formState.isDirty && !!formState?.errors?.datacenter}
          InputLabelProps={{ shrink: true }}
         />
        )}
       />
      )}
     />
    );
   case 2:
    return (
     <Controller
      name="format"
      control={control}
      render={({ field }) => (
       <FormControl fullWidth>
        <InputLabel>
         <FormattedMessage id="volumes.add.format" />
        </InputLabel>
        <Select
         {...field}
         value={watch("format")}
         label={<FormattedMessage id="volumes.add.format" />}
         onChange={(e) => setValue("format", e.target.value)}>
         <MenuItem value="ext4" key={"ext4"}>
          ext4
         </MenuItem>
         <MenuItem value="xfs" key={"xfs"}>
          xfs
         </MenuItem>
        </Select>
       </FormControl>
      )}
     />
    );
   case 3:
    return (
     <Stack direction="row" spacing={2} p={2}>
      <Slider
       value={watch("size")}
       onChange={handleSliderChange}
       onChangeCommitted={(e, value) => setValue("size", typeof value === "number" ? value : 10)}
       aria-labelledby="input-slider"
       min={10}
       max={1000}
      />
      <Input
       value={watch("size")}
       size="small"
       onChange={handleInputChange}
       onBlur={handleBlur}
       inputProps={{
        step: 1,
        min: 10,
        max: 2000,
        type: "number",
        "aria-labelledby": "input-slider"
       }}
      />
     </Stack>
    );
   case 4:
    return (
     <Controller
      name="server_id"
      control={control}
      rules={{ required: true }}
      render={({ field }) => (
       <Autocomplete
        fullWidth={true}
        autoHighlight
        sx={{ my: 2 }}
        onChange={(e, value) =>
         isNil(value) ? setValue("server_id", 0) : setValue("server_id", value.server_id)
        }
        options={servers
         .filter((element) => element.datacenter_location_name === getValues("datacenter"))
         .map((element) => {
          return {
           server_id: element.server_id,
           label: element.server_name
          };
         })}
        renderInput={(params) => (
         <TextField
          {...params}
          {...field}
          label={<FormattedMessage id="volumes.selectServer" />}
          error={formState.isDirty && !!formState?.errors?.server_id}
          InputLabelProps={{ shrink: true }}
         />
        )}
       />
      )}
     />
    );
  }
 };

 const disableNextButton = (index: number) => {
  switch (index) {
   case 0:
    return watch("name").length === 0 || (firstCheck && !nameChecked);
   case 1:
    return watch("datacenter") === "";
   case 2:
    return watch("format") === "";
   case 3:
    return watch("size") === 0;
   default:
    return false;
  }
 };

 const checkDisableConfirm = () => {
  return (
   watch("name") === "" ||
   watch("datacenter") === "" ||
   watch("format") === "" ||
   watch("size") === 0
  );
 };

 const renderServerChecked = () => {
  if (nameChecked) {
   return (
    <Alert severity="success">
     <FormattedMessage id="volumes.add.nameAvailable" />
    </Alert>
   );
  } else {
   return (
    <Alert severity="error">
     <FormattedMessage id="volumes.add.nameNotAvailable" />
    </Alert>
   );
  }
 };

 return !isLoading ? (
  <Container maxWidth={false} sx={{ mx: 2, width: "auto" }}>
   <Stack mt={2}>
    <SectionsBreadCrumbs
     links={[
      { name: "home", url: "/" },
      { name: "addons", url: "/addons/volumes" },
      { name: "volumes", url: "/addons/volumes" },
      { name: "add", url: "/addons/volumes/add" }
     ]}
    />
   </Stack>
   <Grid container spacing={3} sx={{ mt: 3 }}>
    <Grid xs={desktopViewPort ? 3 : 12} sx={{ textAlign: "left" }}>
     <Typography variant="unboundedTitle" fontSize={18} fontWeight="bold">
      <FormattedMessage id={"addons.network.add.addNewNetwork"} />
     </Typography>
     {desktopViewPort && (
      <Stepper activeStep={activeStep} orientation="vertical">
       {volumesSteps.map((step, index) => (
        <Step key={step.label}>
         <StepLabel>
          <FormattedMessage id={step.label} />
         </StepLabel>
        </Step>
       ))}
      </Stepper>
     )}
    </Grid>
    <Grid xs={desktopViewPort ? 6 : 12}>
     <Paper elevation={1} sx={{ borderRadius: "5px", p: 2, textAlign: "left" }}>
      <Typography variant="unboundedTitle" fontSize={18}>
       <FormattedMessage id={volumesSteps[activeStep].label} />
      </Typography>
      {!stepLoading ? (
       <Stack mt={2}>
        <Stack spacing={2}>
         <Typography variant="subtitle2">
          <FormattedMessage id={volumesSteps[activeStep].description} />
         </Typography>
         {activeStep === 2 && (
          <Alert severity="info">
           <Typography variant="subtitle2">
            <FormattedMessage id="addons.volumes.windowsInfo" />
           </Typography>
          </Alert>
         )}
         {renderStepContent(activeStep)}
         {activeStep === 0 && firstCheck && renderServerChecked()}
        </Stack>
        <Stack direction="row" justifyContent="flex-end" alignItems="center">
         {activeStep !== volumesSteps.length - 1 ? (
          <Button
           disabled={disableNextButton(activeStep)}
           onClick={() => (nameChecked ? handleNext() : handleCheckName())}
           sx={{ mt: 1, mr: 1 }}>
           {activeStep === 0 && !nameChecked ? (
            <FormattedMessage id="volumes.checkName" />
           ) : (
            <FormattedMessage id="volumes.continue" />
           )}
          </Button>
         ) : (
          <></>
         )}
         {activeStep === 4 && (
          <>
           <Button sx={{ mt: 1, mr: 1 }} disabled={checkDisableConfirm()} onClick={handleOpen}>
            <FormattedMessage id="volumes.confirmVolume" />
           </Button>
           <Button onClick={handleReset} sx={{ mt: 1, mr: 1 }}>
            <FormattedMessage id="volumes.volumeReset" />
           </Button>
          </>
         )}
         <Button disabled={activeStep === 0} onClick={handleBack} sx={{ mt: 1, mr: 1 }}>
          <FormattedMessage id="volumes.back" />
         </Button>
        </Stack>
       </Stack>
      ) : (
       <Stack mt={2}>
        <Stack spacing={2}>
         <Typography>
          <FormattedMessage id="server.checkExecution" />
         </Typography>
         {renderStepContent(activeStep)}
         <CircularProgress />
        </Stack>
       </Stack>
      )}
     </Paper>
    </Grid>
    <Grid xs={desktopViewPort ? 3 : 12}>
     <Paper
      elevation={1}
      sx={{
       p: 2,
       maxHeight: 580,
       position: "sticky",
       borderRadius: "5px"
      }}>
      <Stack textAlign="left">
       <Typography variant="unboundedTitle" fontWeight="bold" fontSize={18}>
        <FormattedMessage id="volumes.add.recap" />
       </Typography>
      </Stack>
      <Divider sx={{ my: 1 }} />
      <Stack textAlign="left" mb={1}>
       <Typography variant="unboundedTitle" fontSize={14}>
        <FormattedMessage id="volumes.volumeData" />
       </Typography>
      </Stack>
      <Stack direction="row" spacing={2}>
       <Typography variant="subtitle2" minWidth={120} textAlign="start" fontWeight="bold">
        <FormattedMessage id="volumes.name" />:
       </Typography>
       <Typography variant="subtitle2" noWrap>
        {watch("name")}
       </Typography>
      </Stack>
      <Divider sx={{ my: 1 }} />
      <Stack textAlign="left" mb={1}>
       <Typography variant="unboundedTitle" fontSize={14}>
        <FormattedMessage id="volumes.volumeSpecs" />
       </Typography>
      </Stack>
      <Stack direction="row" spacing={2}>
       <Typography variant="subtitle2" minWidth={120} textAlign="start" fontWeight="bold">
        <FormattedMessage id="volumes.datacenter" />:
       </Typography>
       <Typography variant="subtitle2" noWrap>
        {locationList.find((element) => element.name === watch("datacenter"))?.city || ""}
       </Typography>
       <Stack
        component="span"
        className={`fi fi-${
         locationList
          .find((element) => element.name === watch("datacenter"))
          ?.country.toLocaleLowerCase() || ""
        }`}
       />
      </Stack>
      <Stack direction="row" spacing={2}>
       <Typography variant="subtitle2" minWidth={120} textAlign="start" fontWeight="bold">
        <FormattedMessage id="volumes.format" />:
       </Typography>
       <Typography variant="subtitle2" noWrap>
        {watch("format")}
       </Typography>
      </Stack>
      <Stack direction="row" spacing={2}>
       <Typography variant="subtitle2" minWidth={120} textAlign="start" fontWeight="bold">
        <FormattedMessage id="volumes.linkedServer" />:
       </Typography>
       <Typography variant="subtitle2" noWrap>
        {watch("server_id") === 0 ? (
         <FormattedMessage id="volumes.noServerLinked" />
        ) : (
         servers.find((element) => element.server_id === watch("server_id"))?.server_name
        )}
       </Typography>
      </Stack>
      <Divider sx={{ my: 1 }} />
      <Stack textAlign="left" mb={1}>
       <Typography variant="unboundedTitle" fontSize={14}>
        <FormattedMessage id="volumes.volumeSize" />
       </Typography>
      </Stack>
      <Stack direction="row" spacing={2}>
       <Typography variant="subtitle2" minWidth={120} textAlign="start" fontWeight="bold">
        <FormattedMessage id="volumes.size" />:
       </Typography>
       <Typography variant="subtitle2">
        <FormattedNumber value={watch("size")} /> GB
       </Typography>
      </Stack>
      <Stack direction="row" spacing={2} alignItems="center">
       <Typography variant="subtitle2" minWidth={120} textAlign="start" fontWeight="bold">
        <FormattedMessage id="volumes.add.price" />
       </Typography>
       <Typography variant="subtitle2">
        <FormattedNumber value={price} style={"currency"} currency="EUR" />
       </Typography>
      </Stack>
     </Paper>
    </Grid>
   </Grid>
   <AppModal
    open={openModal}
    close={handleClose}
    title={intl.formatMessage({ id: "volumes.createNewVolume" })}
    handleClose={handleClose}
    disabled={loading}
    handleConfirm={handleConfirm}>
    <Stack>
     <FormattedMessage id="volumes.confirmCreation" />
     <Stack direction="row" spacing={2}>
      <Typography minWidth={150} textAlign="start" fontWeight="bold">
       <FormattedMessage id="server.add.balance" />
      </Typography>
      <Typography>
       <FormattedNumber value={userBalance} style={"currency"} currency="EUR" />
      </Typography>
     </Stack>
     <Stack direction="row" spacing={2} alignItems="center">
      <Typography minWidth={150} textAlign="start" fontWeight="bold">
       <FormattedMessage id="server.add.totalPrice" />
      </Typography>
      <Typography sx={{ textDecoration: couponData ? "line-through" : "none" }}>
       <FormattedNumber value={price} style={"currency"} currency="EUR" />
      </Typography>
     </Stack>
     {couponData && (
      <Stack direction="row" spacing={2} alignItems="center">
       <Typography minWidth={150} textAlign="start" fontWeight="bold">
        <FormattedMessage id="app.discountedPrice" />
       </Typography>
       <Typography>
        <FormattedNumber
         value={calculateDiscount(price, couponData.percent)}
         style={"currency"}
         currency="EUR"
        />
       </Typography>
      </Stack>
     )}
     <CheckCoupon setCouponData={(id, percent) => setCouponData({ id: id, percent: percent })} />
    </Stack>
   </AppModal>
   <UserWaiting openLoader={loading} />
  </Container>
 ) : (
  <></>
 );
};

export default VolumesWizard;
